import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, Dialog } from '@dreamtree-org/twreact-ui';
import PageHero from '@/components/site/PageHero';
import { galleryCategories } from '@site/data';
import { useGallery } from '@api/public';
import { usePage } from '@site/content';

export default function Gallery() {
  const items = useGallery();
  const { hero } = usePage('gallery');
  const [category, setCategory] = useState('All');
  const [active, setActive] = useState(null);

  const visible = useMemo(
    () => (category === 'All' ? items : items.filter((item) => item.category === category)),
    [items, category],
  );

  const step = (delta) => setActive((i) => (i + delta + visible.length) % visible.length);
  const current = active === null ? null : visible[active];

  return (
    <>
      <PageHero {...hero} />

      <section className="py-14 sm:py-20">
        <div className="site-container">
          <div role="group" aria-label="Filter photos by category" className="flex flex-wrap gap-2">
            {galleryCategories.map((name) => (
              <Button
                key={name}
                size="sm"
                variant={category === name ? 'primary' : 'outline'}
                className="!rounded-full"
                aria-pressed={category === name}
                onClick={() => setCategory(name)}
              >
                {name}
              </Button>
            ))}
          </div>

          <ul className="mt-8 columns-1 gap-4 sm:columns-2 lg:columns-3 xl:columns-4">
            {visible.map((item, index) => (
              <li key={item.src} className="mb-4 break-inside-avoid">
                <button
                  type="button"
                  onClick={() => setActive(index)}
                  className="group block w-full overflow-hidden rounded-2xl bg-ink-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                  aria-label={`Open photo: ${item.alt}`}
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    loading="lazy"
                    className="w-full transition-transform duration-500 group-hover:scale-105"
                  />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Dialog isOpen={current !== null} onClose={() => setActive(null)} title={current?.category} size="xl">
        {current && (
          <div className="relative">
            <img src={current.src} alt={current.alt} className="max-h-[70vh] w-full rounded-xl object-contain" />
            <div className="mt-4 flex items-center justify-between gap-4">
              <p className="text-sm text-ink-500">{current.alt}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" aria-label="Previous photo" onClick={() => step(-1)}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button variant="outline" size="icon" aria-label="Next photo" onClick={() => step(1)}>
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
}
